// Referee Profile — stat-bar list of a referee's figures (Phase 3 → M2 wave 7).
//
// The stat-bar type (like Team Discipline): a header + five bars (YC, RC, Fouls,
// Pens, Fouls/Card) scaled to the league-wide max. `.stat-bar-fill` has a CSS
// `transition: width`, so the width is set inline and CSS animates on a control
// change — no D3. Emits the same DOM + classes as the legacy renderer (verified
// by the VRT). Data now comes from app-api via `useWidgetData` (was the
// `getRefereeProfileData` getter — the endpoint returns all referees); ref + rate
// shape it client-side, no refetch.

import { shapeRefereeProfile, type RefereeProfileRow } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";
import type { ReactNode } from "react";

export interface RefereeProfileProps {
  /** Fixture — the endpoint is fixture-scoped (referee data is the same across
   *  fixtures, but the cache item is materialised per fixture). */
  fixture: string;
  /** Referee toggle value; defaults to the first referee. */
  ref?: string | null;
  /** Rate toggle: "Per Game" | "Total"; defaults to "Per Game". */
  rate?: string | null;
}

// Icon + bar colour per stat, matching the legacy mkRow calls. The card icons
// carry `card-icon-justify-end` (legacy YC_ICON/RC_ICON).
const ROW_META: Record<RefereeProfileRow["key"], { icon: ReactNode; colour: string }> = {
  yc: { icon: <div className="card-yc card-icon-justify-end" />, colour: "#f1c40f" },
  rc: { icon: <div className="card-rc card-icon-justify-end" />, colour: "#e74c3c" },
  fouls: { icon: <span className="stat-lbl">Fouls</span>, colour: "#3b82f6" },
  pens: { icon: <span className="stat-lbl">Pens</span>, colour: "#2ecc71" },
  fpc: { icon: <span className="stat-lbl">F/Card</span>, colour: "#9b59b6" },
};

export default function RefereeProfile({
  fixture,
  ref,
  rate,
}: RefereeProfileProps) {
  const chosen = ref || "M. Oliver";
  const { data, isPending, isError, refetch } = useWidgetData(
    "referee-profile",
    useFixtureKey(fixture)
  );

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const shape = shapeRefereeProfile(data ?? {}, chosen, rate);

  if (!shape) {
    return <div className="module-loading">No data for {chosen}</div>;
  }

  return (
    <div className="data-wrapper top-align">
      <div className="stat-section-hdr">{`${shape.ref} – ${shape.games} games`}</div>
      {shape.rows.map((row) => {
        const meta = ROW_META[row.key];
        return (
          <div className="stat-row" key={row.key}>
            <div className="stat-row-icon">{meta.icon}</div>
            <div className="stat-bar-track">
              <div
                className="stat-bar-fill"
                style={{ width: `${row.pct}%`, background: meta.colour }}
              />
            </div>
            <div className="stat-row-val">{row.text}</div>
          </div>
        );
      })}
    </div>
  );
}
